import { Component, OnInit, ElementRef, HostListener, inject } from '@angular/core';
import { Api } from '../../services/api';
import { AlertService } from '../../services/alert.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MdlManageProspect } from '../../components/mdl-manage-prospect/mdl-manage-prospect';
import { MdlDelete } from '../../components/mdl-delete/mdl-delete';
import { MdlManageContact } from '../../components/mdl-manage-contact/mdl-manage-contact';
import { MdlFilterContacts } from '../../components/mdl-filter-contacts/mdl-filter-contacts';

@Component({
    selector: 'app-contacts',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './contacts.html',
    styleUrls: ['./contacts.css']
})
export class Contacts implements OnInit {

    contactos: any[] = [];
    contactosFiltrados: any[] = [];
    isLoading: boolean = true;
    errorMessage: string = '';
    showRecentDropdown: boolean = false;
    selectedItem: string = 'todas';
    openedMenuId: string | null = null;
    searchTerm: string = '';
    sortColumn: string = '';
    sortDirection: 'asc' | 'desc' = 'asc';
    activeFilters: any = null;

    constructor(
        private api: Api,
        private elementRef: ElementRef,
        private router: Router,
        private alertService: AlertService
    ) {
        this.cargarContacts();
    }

    readonly dialog = inject(MatDialog);

    ngOnInit(): void {
        this.initializeFilters();
    }


    private initializeFilters(): void {
        if (!this.activeFilters) {
            this.activeFilters = {
                allAccounts: true,
                allOwners: true,
                creationDateRange: 'all',
                phoneFilter: 'all',
                emailFilter: 'all',
                accounts: {},
                owners: {}
            };
        }
    }

    openFilterModal(): void {
        const dialogRef = this.dialog.open(MdlFilterContacts, {
            minWidth: '600px',
            maxHeight: '80vh',
            panelClass: 'mat-dlg-class',
            data: {
                contactos: this.contactos,
                filters: this.activeFilters
            }
        });

        dialogRef.afterClosed().subscribe((filters: any) => {
            if (filters) {
                this.activeFilters = filters;
                this.applyAdvancedFilters();
                this.alertService.success('Filtros aplicados', 'Filtros aplicados correctamente');
            }
        });
    }

    private applyAdvancedFilters(): void {
        let filteredContacts = [...this.contactos];

        if (!this.activeFilters.allAccounts) {
            const selectedAccounts = Object.keys(this.activeFilters.accounts).filter(key => this.activeFilters.accounts[key]);
            if (selectedAccounts.length > 0) {
                filteredContacts = filteredContacts.filter(contacto => 
                    selectedAccounts.includes(contacto.cuenta?.nombre_empresa)
                );
            }
        }

        if (!this.activeFilters.allOwners) {
            const selectedOwners = Object.keys(this.activeFilters.owners).filter(key => this.activeFilters.owners[key]);
            if (selectedOwners.length > 0) {
                filteredContacts = filteredContacts.filter(contacto => {
                    const ownerName = `${contacto.ejecutivo?.nombre || ''} ${contacto.ejecutivo?.apellido || ''}`.trim();
                    return selectedOwners.includes(ownerName);
                });
            }
        }

        
        if (this.activeFilters.creationDateRange !== 'all') {
            filteredContacts = this.filterByCreationDate(filteredContacts, this.activeFilters.creationDateRange);
        }

        
        if (this.activeFilters.phoneFilter !== 'all') {
            filteredContacts = this.filterByPhone(filteredContacts, this.activeFilters.phoneFilter);
        }

        
        if (this.activeFilters.emailFilter !== 'all') {
            filteredContacts = this.filterByEmail(filteredContacts, this.activeFilters.emailFilter);
        }

        this.contactosFiltrados = filteredContacts;
        
        if (this.sortColumn) {
            this.applySorting();
        }
    }

    private filterByCreationDate(contacts: any[], dateRange: string): any[] {
        const hoy = new Date();
        
        switch (dateRange) {
            case 'today':
                return contacts.filter(contacto => {
                    if (!contacto.fecha_creacion) return false;
                    const fechaCreacion = new Date(contacto.fecha_creacion);
                    return fechaCreacion.toDateString() === hoy.toDateString();
                });
            
            case '7d':
                const sieteDiasAtras = new Date(hoy);
                sieteDiasAtras.setDate(hoy.getDate() - 7);
                return contacts.filter(contacto => {
                    if (!contacto.fecha_creacion) return false;
                    const fechaCreacion = new Date(contacto.fecha_creacion);
                    return fechaCreacion >= sieteDiasAtras && fechaCreacion <= hoy;
                });
            
            case '30d':
                const treintaDiasAtras = new Date(hoy);
                treintaDiasAtras.setDate(hoy.getDate() - 30);
                return contacts.filter(contacto => {
                    if (!contacto.fecha_creacion) return false;
                    const fechaCreacion = new Date(contacto.fecha_creacion);
                    return fechaCreacion >= treintaDiasAtras && fechaCreacion <= hoy;
                });
            
            default:
                return contacts;
        }
    }

    private filterByPhone(contacts: any[], phoneFilter: string): any[] {
        switch (phoneFilter) {
            case 'withPhone':
                return contacts.filter(contacto => contacto.telefono && contacto.telefono.trim() !== '');
            case 'withoutPhone':
                return contacts.filter(contacto => !contacto.telefono || contacto.telefono.trim() === '');
            default:
                return contacts;
        }
    }

    private filterByEmail(contacts: any[], emailFilter: string): any[] {
        switch (emailFilter) {
            case 'withEmail':
                return contacts.filter(contacto => contacto.email && contacto.email.trim() !== '');
            case 'withoutEmail':
                return contacts.filter(contacto => !contacto.email || contacto.email.trim() === '');
            default:
                return contacts;
        }
    }

    sortByColumn(column: string): void {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }

        this.applySorting();
    }

    private applySorting(): void {
        if (!this.sortColumn) return;

        this.contactosFiltrados = [...this.contactosFiltrados].sort((a, b) => {
            let valueA = this.getSortValue(a, this.sortColumn);
            let valueB = this.getSortValue(b, this.sortColumn);

            if (valueA < valueB) {
                return this.sortDirection === 'asc' ? -1 : 1;
            }
            if (valueA > valueB) {
                return this.sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }

    private getSortValue(contacto: any, column: string): any {
        switch (column) {
            case 'nombre':
                return `${contacto.nombre} ${contacto.apellido}`.toLowerCase();
            
            case 'cuenta.nombre_empresa':
                return contacto.cuenta?.nombre_empresa?.toLowerCase() || '';
            
            case 'telefono':
                return contacto.telefono || '';
            
            case 'email':
                return contacto.email?.toLowerCase() || '';
            
            case 'ejecutivo.nombre':
                return `${contacto.ejecutivo?.nombre || ''} ${contacto.ejecutivo?.apellido || ''}`.toLowerCase();
            
            default:
                return contacto[column] || '';
        }
    }

    onSearchChange(): void {
        if (!this.searchTerm.trim()) {
            if (this.activeFilters) {
                this.applyAdvancedFilters();
            } else {
                this.contactosFiltrados = [...this.contactos];
            }
        } else {
            const searchLower = this.searchTerm.toLowerCase().trim();
            this.contactosFiltrados = this.contactos.filter(contacto => 
                contacto.nombre?.toLowerCase().includes(searchLower) ||
                contacto.apellido?.toLowerCase().includes(searchLower) ||
                contacto.cuenta?.nombre_empresa?.toLowerCase().includes(searchLower) ||
                contacto.telefono?.includes(searchLower) ||
                contacto.email?.toLowerCase().includes(searchLower) ||
                contacto.ejecutivo?.nombre?.toLowerCase().includes(searchLower) ||
                contacto.ejecutivo?.apellido?.toLowerCase().includes(searchLower)
            );
        }
        
        if (this.sortColumn) {
            this.applySorting();
        }
    }

    hasActiveFilters(): boolean {
        if (!this.activeFilters) return false;
        
        const hasAccountFilter = !this.activeFilters.allAccounts && 
            Object.values(this.activeFilters.accounts).some(val => val === true);
        const hasOwnerFilter = !this.activeFilters.allOwners && 
            Object.values(this.activeFilters.owners).some(val => val === true);
        const hasDateFilter = this.activeFilters.creationDateRange !== 'all';
        const hasPhoneFilter = this.activeFilters.phoneFilter !== 'all';
        const hasEmailFilter = this.activeFilters.emailFilter !== 'all';
        
        return hasAccountFilter || hasOwnerFilter || hasDateFilter || hasPhoneFilter || hasEmailFilter || this.searchTerm.trim() !== '';
    }

    clearFilters(): void {
        this.activeFilters = null;
        this.searchTerm = '';
        this.sortColumn = '';
        this.sortDirection = 'asc';
        this.initializeFilters();
        this.contactosFiltrados = [...this.contactos];
        this.alertService.success('Filtros limpiados', 'Los filtros han sido limpiados');
    }


    cargarContacts(): void {
        console.log('[DEBUG] cargarContacts fue llamado');
        this.api.showLoading();
        this.isLoading = true;
        this.errorMessage = '';

        this.api.getContacts().subscribe({
            next: (response: any) => {
                console.log('Respuesta del API:', response);
                this.contactos = (response.contactos || response).map((contact: any) => {
                    return {
                        ...contact,
                        id: contact.contact_id || contact.id 
                    };
                });

                if (this.activeFilters) {
                    this.applyAdvancedFilters();
                } else {
                    this.contactosFiltrados = [...this.contactos];
                }

                setTimeout(() => {
                    this.api.hideLoading();
                    this.isLoading = false;
                }, 500);
            },
            error: (error) => {
                console.error('Error al cargar contactos:', error);
                this.errorMessage = 'Error al cargar los contactos. Por favor, inténtelo de nuevo más tarde.';
                this.isLoading = false;
                this.api.hideLoading();
            }
        });
    }



    private esUUIDValido(id: string): boolean {
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return uuidRegex.test(id);
    }

    mostrarMensaje(mensaje: string, tipo: 'exito' | 'error' | 'advertencia'): void {
        switch (tipo) {
            case 'exito':
                this.alertService.success('Éxito', mensaje);
                break;
            case 'error':
                this.alertService.error('Error', mensaje);
                break;
            case 'advertencia':
                this.alertService.warning('Advertencia', mensaje);
                break;
        }
    }

    toggleRecentDropdown(): void {
        this.showRecentDropdown = !this.showRecentDropdown;
    }

    selectItem(key: string): void {
        this.selectedItem = key;
        this.showRecentDropdown = false; 
    }

    toggleActionsMenu(id: string): void {
        this.openedMenuId = this.openedMenuId === id ? null : id;
        this.showRecentDropdown = false; 
    }

    openMdlCreateContact() {
        this.dialog.open(MdlManageContact,{
            minWidth: '800px',
            maxHeight: '900px',
            panelClass: 'mat-dlg-class',
            data:{
                mode: 'create'
            }
        });
    }

    openMdlDeleteContact(id: string) {
        this.dialog.open(MdlDelete, {
            minWidth: '500px',
            minHeight: '260px',
            panelClass: 'mat-dlg-class',
            data:{
                mode: 'contact',
                id: id
            }
        });
    }

    verDetalleContacto(id: string, event?: MouseEvent): void  {
        if (event) {
            const target = event.target as HTMLElement;
            const isCheckbox = target.tagName === 'INPUT' && target.getAttribute('type') === 'checkbox';
            const isDropdown = target.closest('.dropdown-container') !== null;
            
            if (isCheckbox || isDropdown) {
                return;
            }
        }

        if (!id || !this.esUUIDValido(id)) {
            this.mostrarMensaje('ID de contacto no válido', 'error');
            return;
        }

        this.router.navigate(['contact', id]).then(navigationResult => {
            if (!navigationResult) {
                this.mostrarMensaje('No se pudo cargar el detalle del contacto', 'error');
                console.error('Error de navegación a contact/', id);
            }
        });
    }

    updateContact(data:any){
        console.log("Click");
        this.dialog.open(MdlManageContact,{
            minWidth: '800px',
            maxHeight: '900px',
            panelClass: 'mat-dlg-class',
            data:{
                mode: 'edit',
                contact: data
            }
        });
    }

    @HostListener('document:click', ['$event.target'])
    onClickOutside(target: EventTarget | null): void {
        if (!(target instanceof HTMLElement)) return;
        
        const clickedInsideRecent = this.elementRef.nativeElement.querySelector('.dropdown-trigger')?.contains(target);
        const clickedInsideActions = this.elementRef.nativeElement.querySelector('.dots-button')?.contains(target);
        
        if (!clickedInsideRecent) {
            this.showRecentDropdown = false;
        }
        
        if (!clickedInsideActions) {
            this.openedMenuId = null;
        }
    }
}